import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import { ArrowLeft, Check, Loader2, Palette, Sparkles, Trash2, Wrench } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const COLOR_PRESETS = [
  { hex: '#0e7490', name: 'Cyan / Ocean' },
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#10b981', name: 'Emerald / Green' },
  { hex: '#f59e0b', name: 'Amber / Gold' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#64748b', name: 'Slate / Neutral' },
];

interface SkillFormPageProps {
  mode: 'create' | 'edit';
}

export default function SkillFormPage({ mode }: SkillFormPageProps) {
  const { orgId, workspaceId, skillId } = useParams<{
    orgId: string;
    workspaceId: string;
    skillId?: string;
  }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const basePath = `/org/${orgId}/workspace/${workspaceId}/skills`;
  const numSkillId = skillId ? parseInt(skillId, 10) : 0;

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [exampleJobTypes, setExampleJobTypes] = useState('');
  const [color, setColor] = useState('#0e7490');

  const { data: existingSkill, isLoading: isSkillLoading } = trpc.jobPlan.skills.getById.useQuery(
    { id: numSkillId },
    { enabled: mode === 'edit' && numSkillId > 0 }
  );

  useEffect(() => {
    if (mode === 'edit' && existingSkill) {
      setName(existingSkill.name);
      setDescription(existingSkill.description || '');
      setKeywords(existingSkill.keywords || '');
      setExampleJobTypes(existingSkill.exampleJobTypes || '');
      setColor(existingSkill.color || '#0e7490');
    }
  }, [mode, existingSkill]);

  const createSkillMutation = trpc.jobPlan.skills.create.useMutation({
    onSuccess: (newSkill) => {
      toast.success(`Skill "${newSkill.name}" created`);
      utils.jobPlan.skills.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateSkillMutation = trpc.jobPlan.skills.update.useMutation({
    onSuccess: () => {
      toast.success('Skill updated successfully');
      utils.jobPlan.skills.list.invalidate();
      utils.jobPlan.skills.getById.invalidate({ id: numSkillId });
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteSkillMutation = trpc.jobPlan.skills.delete.useMutation({
    onSuccess: () => {
      toast.success('Skill deleted');
      utils.jobPlan.skills.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please provide a skill name');
      return;
    }

    if (mode === 'create') {
      createSkillMutation.mutate({
        name: name.trim(),
        description: description.trim() || undefined,
        keywords: keywords.trim() || undefined,
        exampleJobTypes: exampleJobTypes.trim() || undefined,
        color,
      });
    } else {
      if (!numSkillId) return;
      updateSkillMutation.mutate({
        id: numSkillId,
        name: name.trim(),
        description: description.trim() || undefined,
        keywords: keywords.trim() || undefined,
        exampleJobTypes: exampleJobTypes.trim() || undefined,
        color,
      });
    }
  };

  const handleDelete = () => {
    if (!numSkillId) return;
    if (
      confirm(
        'Are you sure you want to delete this skill? It will be unlinked from operators and jobs.'
      )
    ) {
      deleteSkillMutation.mutate({ id: numSkillId });
    }
  };

  const isSaving = createSkillMutation.isPending || updateSkillMutation.isPending;

  if (mode === 'edit' && isSkillLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading skill details...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Sticky Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={basePath}>
              <ArrowLeft className="h-4 w-4" />
              <span>Skills Catalog</span>
            </Link>
          </Button>

          <span className="text-muted-foreground/50">/</span>

          <h1 className="text-lg font-bold tracking-tight text-foreground truncate max-w-md">
            {mode === 'create' ? 'Create New Skill' : `Edit ${existingSkill?.name || 'Skill'}`}
          </h1>

          <Badge
            variant="outline"
            style={{
              backgroundColor: color ? `${color}15` : undefined,
              color: color || undefined,
              borderColor: color ? `${color}40` : undefined,
            }}
            className="text-xs font-semibold px-2.5 py-0.5"
          >
            {name || 'Skill Preview'}
          </Badge>
        </div>

        <div className="flex items-center gap-2.5">
          {mode === 'edit' && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10 border-destructive/30 gap-1.5"
              onClick={handleDelete}
              disabled={deleteSkillMutation.isPending || isSaving}
            >
              {deleteSkillMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              <span>Delete</span>
            </Button>
          )}

          <Button variant="outline" size="sm" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>

          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
            className="gap-1.5"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{mode === 'create' ? 'Create Skill' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      {/* Main Full Page Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-4xl w-full mx-auto space-y-6">
        {/* Section 1: Skill Name & Scope */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4 text-primary" />
              <span>Skill Name & Details</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Give this skill or qualification a clear descriptive name and scope notes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Skill / Qualification Name *
              </label>
              <Input
                placeholder="e.g. Working at Heights, Hazardous Chemical Handling, High Voltage Level 2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Description & Compliance Requirements
              </label>
              <Input
                placeholder="Optional regulatory requirements, license numbers, or equipment notes..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="text-[11px] text-muted-foreground">
                Detailed notes explaining the certification criteria for this skill.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: AI Ingestion Context & Semantic Matching */}
        <Card className="border-primary/20 bg-primary/[0.02]">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>AI Semantic Matching & Related Job Types</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Help the AI extraction engine automatically recognize and attach this skill when
              parsing screenshots, spreadsheets, or job details from legacy software.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Keywords, Synonyms & Trade Acronyms
              </label>
              <Input
                placeholder="e.g. HVAC, aircon, heat pump, refrigeration, chiller, compressor, duct"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
              <span className="text-[11px] text-muted-foreground">
                Comma-separated technical terms or alternative names commonly used in work orders.
              </span>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Example Jobs & Project Descriptions
              </label>
              <textarea
                placeholder="e.g. Split system install, commercial fridge diagnostic, rooftop AC unit maintenance, refrigerant gas leak check, condenser fan overhaul"
                value={exampleJobTypes}
                onChange={(e) => setExampleJobTypes(e.target.value)}
                rows={3}
                className="w-full p-2.5 text-xs rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              />
              <span className="text-[11px] text-muted-foreground">
                Describe typical work order requests, issue scopes, or projects that require this
                skill.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Visual Badge Color */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              <span>Visual Badge Theme & Swatch</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Color coding allows planners and operators to quickly recognize capabilities across
              the calendar, kanban, and map.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-foreground">Theme Color</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {COLOR_PRESETS.map((preset) => {
                  const isSelected = color.toLowerCase() === preset.hex.toLowerCase();
                  return (
                    <button
                      key={preset.hex}
                      type="button"
                      onClick={() => setColor(preset.hex)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/30 bg-muted/40 font-semibold'
                          : 'border-border/60 hover:bg-muted/30'
                      }`}
                    >
                      <span
                        className="h-4 w-4 rounded-full shrink-0 flex items-center justify-center text-white"
                        style={{ backgroundColor: preset.hex }}
                      >
                        {isSelected && <Check className="h-2.5 w-2.5" />}
                      </span>
                      <span className="text-xs truncate">{preset.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Hex input */}
            <div className="flex items-center gap-3 pt-2 border-t border-border/40">
              <label className="text-xs font-medium text-foreground shrink-0">
                Custom Hex Code:
              </label>
              <div className="flex items-center gap-2 max-w-xs">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-8 w-8 rounded border border-border cursor-pointer p-0.5"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-8 text-xs font-mono w-28"
                  placeholder="#0e7490"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Live Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>Badge Preview in UI Projections</span>
            </CardTitle>
            <CardDescription className="text-xs">
              How this skill tag will appear on operator cards, job lists, and timeline Gantt views.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-4 rounded-lg border border-border/60 bg-muted/20 flex flex-wrap items-center gap-3">
              <Badge
                variant="outline"
                style={{
                  backgroundColor: color ? `${color}15` : undefined,
                  color: color || undefined,
                  borderColor: color ? `${color}40` : undefined,
                }}
                className="text-xs font-semibold px-3 py-1"
              >
                {name.trim() || 'Sample Skill Tag'}
              </Badge>

              <Badge
                variant="secondary"
                style={{
                  backgroundColor: color ? `${color}25` : undefined,
                  color: color || undefined,
                }}
                className="text-[11px] px-2 py-0.5 font-medium"
              >
                {name.trim() || 'Sample Skill Tag'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 pt-4 pb-12 border-t border-border/40">
          <Button variant="outline" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !name.trim()} className="gap-2">
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{mode === 'create' ? 'Create Skill' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
